// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { BaseSearchableFilter } from '@/filters/base-searchable-filter';
import { useSparseProjectList } from '@/project/hooks/use-sparse-project-list';
import { useMemo } from 'react';

interface Props {
  /**
   * An array of strings representing the currently selected project IDs.
   */
  value: string[];

  /**
   * The label for the filter menu.
   */
  label?: string;

  /**
   * A callback fired when the selected project IDs change.
   */
  onChange: (value: string[]) => void;

  /**
   * Whether to show the search input. Defaults to true.
   */
  enableSearch?: boolean;

  /**
   * Whether to show a "Not set" option as the first item. When selected,
   * it will pass "null" as an ID to the server. Defaults to false.
   */
  enableNotSet?: boolean;
}

/**
 * A searchable filter for selecting zero or more projects from the remote API.
 */
export function ProjectFilter(props: Props) {
  const { projects, isLoading } = useSparseProjectList();

  // Memoized on the projects: the options no longer carry selection
  const options = useMemo(() => {
    const projectOptions = projects.map(({ id, name, isArchived }) => ({
      value: id,
      label: name,
      isArchived,
      color: isArchived ? 'text.secondary' : undefined,
    }));

    return props.enableNotSet
      ? [
          { value: 'null', label: 'Not set', isSpecial: true },
          ...projectOptions,
        ]
      : projectOptions;
  }, [projects, props.enableNotSet]);

  const handleChangeOption = (id: string, isActive: boolean) => {
    if (isActive) {
      return props.onChange([...props.value, id]);
    }

    props.onChange(props.value.filter((projectId) => projectId !== id));
  };

  return (
    <BaseSearchableFilter
      label={props.label ?? 'Projects'}
      options={options}
      selectedValues={props.value}
      onChangeOption={handleChangeOption}
      searchPlaceholder="Search projects..."
      enableSearch={props.enableSearch}
      isLoading={isLoading}
    />
  );
}
